import { ICustomBinding } from "#/DefaultViewExtensionInterfaces";
import {
    WebComponentBindingOptions,
    WebComponentEventHandler,
    WebComponentPropertyBinding,
    WebComponentTwoWayBinding
} from '#/CoreFrameInterfaces';

export default class WebComponentBinding implements ICustomBinding {
    private static globalDependencies: Record<string, any> = {};

    /**
     * Erzeugt ein HTML-Element und wendet das WebComponent-Binding direkt darauf an.
     * @param tagName Der Tag des Elementes, bspw. 'my-custom-element'
     * @param options Die Binding-Optionen mit entsprechenden Werten für Attribute und Events
     */
    public static createElement<T = any>(tagName: string, options: WebComponentBindingOptions<T>): JQuery<HTMLElement>;
    public static createElement<K extends keyof HTMLElementTagNameMap, T = any>(tagName: K, options: WebComponentBindingOptions<T>): JQuery<HTMLElementTagNameMap[K]> {
        const el = document.createElement(tagName);
        ko.applyBindingsToNode(el, { webcomponent: options });
        return $(el) as JQuery<HTMLElementTagNameMap[K]>;
    }

    /**
     * Registriert eine globale Dependency, welche bei jedem Anwenden des Bindings bereitgestellt wird.
     */
    public static registerGlobalDependency<T>(name: string, value: T): void {
        this.globalDependencies[name] = value;
    }

    public static createTwoWayBinding<T>(observable: KnockoutObservable<T>, eventName: string): WebComponentTwoWayBinding<T> {
        return { observable, eventName };
    }

    initBinding(): void {
        ko.bindingHandlers.webcomponent = {
            init: (element: HTMLElement, valueAccessor: () => WebComponentBindingOptions, allBindings: KnockoutAllBindingsAccessor, viewModel: any, bindingContext: KnockoutBindingContext) => {
                const value = valueAccessor();
                const subscriptions: KnockoutSubscription[] = [];
                const eventListeners: { [name: string]: Function } = {};

                const dependencies = { ...WebComponentBinding.globalDependencies, ...value.dependencies };
                for (const [dependencyName, dependency] of Object.entries(dependencies)) {
                    element[dependencyName] = dependency;
                }

                for (const [propertyName, propertyBinding] of Object.entries(value.properties ?? {})) {
                    // Auf Events der WebComponent registrieren (in Angular: @Output)
                    if (this.isEventHandler(propertyBinding)) {
                        const listener = (e: any) => propertyBinding(e.detail);
                        element.addEventListener(propertyName, listener);
                        eventListeners[propertyName] = listener;
                        continue;
                    }

                    const observableOrValue = this.isTwoWayBinding(propertyBinding) ? propertyBinding.observable : propertyBinding;

                    // Attribut-Wert initial setzen
                    element[propertyName] = ko.unwrap(observableOrValue);

                    // Bei Änderung des Observable Wertes Attribut-Wert aktualisieren
                    if (ko.isObservable(observableOrValue)) {
                        const subscription = observableOrValue.subscribe(v => {
                            element[propertyName] = v;
                        });
                        subscriptions.push(subscription);
                    }

                    // Beim Two-Way Binding muss angegeben werden, wie das Event heißt. Wenn sich der Wert in der
                    // WebComponent ändert, wird das Event gefeuert und wir aktualisieren den Wert vom Observable
                    if (this.isTwoWayBinding(propertyBinding) && propertyBinding.eventName) {
                        const listener = (e: any) => observableOrValue(e.detail);
                        element.addEventListener(propertyBinding.eventName, listener);
                        eventListeners[propertyBinding.eventName] = listener;
                    }
                }

                // Disposing
                ko.utils.domNodeDisposal.addDisposeCallback(element, e => {
                    for (const sub of subscriptions) {
                        sub.dispose();
                    }
                    for (const eventName of Object.keys(eventListeners)) {
                        e.removeEventListener(eventName, eventListeners[eventName]);
                    }
                });
            }
        };
    }

    private isTwoWayBinding<T extends object>(obj: WebComponentPropertyBinding<T>): obj is WebComponentTwoWayBinding<T> {
        return obj && 'observable' in obj;
    }

    private isEventHandler<T>(obj: WebComponentPropertyBinding<T>): obj is WebComponentEventHandler<T> {
        return !ko.isObservable(obj) && _.isFunction(obj);
    }
}